if (!customElements.get('dtc-hero-chapters')) {
  customElements.define(
    'dtc-hero-chapters',
    class DtcHeroChapters extends HTMLElement {
      connectedCallback() {
        this.cleanup();

        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.previousButton = this.querySelector('[data-previous]');
        this.nextButton = this.querySelector('[data-next]');
        this.autoplayButton = this.querySelector('[data-autoplay]');
        this.status = this.querySelector('[data-status]');
        this.currentIndex = 0;
        this.interval = Number(this.dataset.interval) || 6000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplayRequested = this.dataset.autoRotate === 'true' && !this.reducedMotion;
        this.isPaused = !this.autoplayRequested;
        this.resumeAfterVisibility = false;
        this.touchStartX = null;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        if (!this.track || this.slides.length === 0) return;

        this.previousButton?.addEventListener('click', () => this.previous(true), { signal });
        this.nextButton?.addEventListener('click', () => this.next(true), { signal });
        this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dot), true), { signal });
        });

        this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
        this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
        this.track.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true, signal });
        document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
        this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });

        this.update(false);
        if (this.autoplayRequested) this.play();
        else this.setAutoplayButton(true);
      }

      disconnectedCallback() {
        this.cleanup();
      }

      cleanup() {
        window.clearInterval(this.timer);
        this.timer = null;
        this.abortController?.abort();
      }

      goTo(index, userInitiated = false) {
        const slideCount = this.slides.length;
        this.currentIndex = (index + slideCount) % slideCount;
        if (userInitiated) this.pause();
        this.update(userInitiated);
      }

      previous(userInitiated = false) {
        this.goTo(this.currentIndex - 1, userInitiated);
      }

      next(userInitiated = false) {
        this.goTo(this.currentIndex + 1, userInitiated);
      }

      update(announce = true) {
        this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;

        this.slides.forEach((slide, index) => {
          const active = index === this.currentIndex;
          slide.setAttribute('aria-hidden', String(!active));
          slide.querySelectorAll('[data-slide-link]').forEach((link) => {
            if (active) link.removeAttribute('tabindex');
            else link.setAttribute('tabindex', '-1');
          });
        });

        this.dots.forEach((dot, index) => {
          const active = index === this.currentIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });

        if (announce && this.status) {
          const heading = this.slides[this.currentIndex].querySelector('.dtc-hero__heading')?.textContent?.trim() || '';
          this.status.textContent = `Chapter ${this.currentIndex + 1} of ${this.slides.length}: ${heading}`;
        }
      }

      play() {
        if (this.reducedMotion || this.slides.length < 2) return;
        window.clearInterval(this.timer);
        this.isPaused = false;
        this.setAutoplayButton(false);
        this.timer = window.setInterval(() => this.next(false), this.interval);
      }

      pause() {
        window.clearInterval(this.timer);
        this.timer = null;
        this.isPaused = true;
        this.setAutoplayButton(true);
      }

      toggleAutoplay() {
        if (this.isPaused) this.play();
        else this.pause();
      }

      setAutoplayButton(paused) {
        if (!this.autoplayButton) return;
        this.autoplayButton.classList.toggle('is-paused', paused);
        this.autoplayButton.setAttribute('aria-label', paused ? 'Play chapters' : 'Pause chapters');
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous(true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next(true);
        }
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0]?.clientX ?? null;
      }

      onTouchEnd(event) {
        if (this.touchStartX === null) return;
        const touchEndX = event.changedTouches[0]?.clientX ?? this.touchStartX;
        const distance = touchEndX - this.touchStartX;
        this.touchStartX = null;

        if (Math.abs(distance) < 45) return;
        if (distance > 0) this.previous(true);
        else this.next(true);
      }

      onVisibilityChange() {
        if (document.hidden) {
          this.resumeAfterVisibility = !this.isPaused;
          window.clearInterval(this.timer);
          this.timer = null;
        } else if (this.resumeAfterVisibility) {
          this.resumeAfterVisibility = false;
          this.play();
        }
      }

      onBlockSelect(event) {
        const slide = event.target.closest('[data-slide]');
        const index = this.slides.indexOf(slide);

        if (index >= 0) this.goTo(index, true);
      }
    }
  );
}
